import React, { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, ArrowUp, ArrowDown, Save, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Editor for the Google Reviews shown on /#reviews (public page).
 * Stored under db.google_reviews.main.reviews[] as {author, rating, relative_time, text}.
 * Also lets admin set the "google_review_url" (the link every card + the CTA button will use).
 */
export default function ReviewsAdmin() {
  const [reviews, setReviews] = useState([]);
  const [reviewUrl, setReviewUrl] = useState("");
  const [rating, setRating] = useState(5.0);
  const [count, setCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/reviews/google");
      setReviews(r.data.reviews || []);
      setReviewUrl(r.data.review_url || "");
      setRating(r.data.rating || 5.0);
      setCount(r.data.count || 0);
      setDirty(false);
    } catch { toast.error("Could not load reviews"); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const patch = (i, k, v) => { setReviews(prev => prev.map((r, j) => j === i ? { ...r, [k]: v } : r)); setDirty(true); };
  const add = () => { setReviews(prev => [{ author: "", rating: 5, relative_time: "recently", text: "" }, ...prev]); setDirty(true); };
  const remove = (i) => {
    if (!window.confirm("Delete this review?")) return;
    setReviews(prev => prev.filter((_, j) => j !== i)); setDirty(true);
  };
  const move = (i, dir) => {
    const j = i + dir; if (j < 0 || j >= reviews.length) return;
    const next = [...reviews]; [next[i], next[j]] = [next[j], next[i]];
    setReviews(next); setDirty(true);
  };

  const saveReviews = async () => {
    setBusy(true);
    try {
      await api.put("/admin/google-reviews", { reviews });
      toast.success(`Saved ${reviews.length} review${reviews.length === 1 ? "" : "s"}`);
      setDirty(false);
    } catch { toast.error("Could not save reviews"); }
    finally { setBusy(false); }
  };

  const saveSettings = async () => {
    setBusy(true);
    try {
      await api.put("/admin/settings", {
        google_review_url: reviewUrl,
        google_rating: Number(rating),
        google_review_count: Number(count),
      });
      toast.success("Google review link + rating saved");
    } catch { toast.error("Could not save settings"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-8" data-testid="reviews-admin">
      {/* Google link + aggregate rating box */}
      <div className="bg-[#0a0a0a] border border-white/10 p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="eyebrow">Google Business Profile</div>
            <div className="text-sm text-white/60 mt-1">Public "View on Google" buttons on every review card link to this URL.</div>
          </div>
          {reviewUrl && (
            <a href={reviewUrl} target="_blank" rel="noreferrer" className="btn-ghost text-xs" data-testid="preview-google-url">
              <ExternalLink size={12} /> Open
            </a>
          )}
        </div>
        <label className="block">
          <span className="text-[0.6rem] tracking-[0.25em] uppercase text-white/60">Google Reviews URL</span>
          <input value={reviewUrl} onChange={e => setReviewUrl(e.target.value)}
            placeholder="https://g.page/r/... or the Google Maps place URL"
            className="mt-1 w-full bg-black border border-white/15 px-3 py-2 text-sm focus:border-[#D4AF37] outline-none"
            data-testid="google-review-url" />
        </label>
        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-[0.6rem] tracking-[0.25em] uppercase text-white/60">Overall rating shown</span>
            <input type="number" step="0.1" min="0" max="5" value={rating}
              onChange={e => setRating(e.target.value)}
              className="mt-1 w-full bg-black border border-white/15 px-3 py-2 text-sm focus:border-[#D4AF37] outline-none"
              data-testid="google-rating" />
          </label>
          <label className="block">
            <span className="text-[0.6rem] tracking-[0.25em] uppercase text-white/60">Total review count</span>
            <input type="number" min="0" value={count}
              onChange={e => setCount(e.target.value)}
              className="mt-1 w-full bg-black border border-white/15 px-3 py-2 text-sm focus:border-[#D4AF37] outline-none"
              data-testid="google-count" />
          </label>
        </div>
        <button onClick={saveSettings} disabled={busy} className="btn-gold text-xs" data-testid="save-google-settings">
          <Save size={12} /> Save Google settings
        </button>
      </div>

      {/* Individual review cards */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="eyebrow">Reviews</div>
            <div className="text-sm text-white/60 mt-1">{reviews.length} card{reviews.length === 1 ? "" : "s"} shown on the public page.</div>
          </div>
          <div className="flex gap-2">
            <button onClick={add} className="btn-ghost text-xs" data-testid="reviews-add">
              <Plus size={12} /> Add review
            </button>
            <button onClick={saveReviews} disabled={busy || !dirty}
              className={`text-xs ${dirty ? "btn-gold" : "btn-ghost opacity-60"}`}
              data-testid="reviews-save-all">
              <Save size={12} /> {dirty ? "Save changes" : "Saved"}
            </button>
          </div>
        </div>

        <div className="space-y-3" data-testid="reviews-list">
          {reviews.map((r, i) => (
            <div key={i} className="bg-[#0a0a0a] border border-white/10 p-5" data-testid={`review-row-${i}`}>
              <div className="flex items-start gap-4">
                <div className="flex flex-col gap-1 pt-1">
                  <button onClick={() => move(i, -1)} disabled={i === 0} title="Move up"
                    className="p-1 text-white/40 hover:text-[#D4AF37] disabled:opacity-30"><ArrowUp size={12} /></button>
                  <button onClick={() => move(i, 1)} disabled={i === reviews.length - 1} title="Move down"
                    className="p-1 text-white/40 hover:text-[#D4AF37] disabled:opacity-30"><ArrowDown size={12} /></button>
                </div>
                <div className="flex-1 grid gap-3 sm:grid-cols-2">
                  <label className="block">
                    <span className="text-[0.6rem] tracking-[0.25em] uppercase text-white/60">Client name</span>
                    <input value={r.author || ""} onChange={e => patch(i, "author", e.target.value)}
                      className="mt-1 w-full bg-black border border-white/15 px-3 py-2 text-sm focus:border-[#D4AF37] outline-none"
                      data-testid={`review-row-${i}-author`} />
                  </label>
                  <label className="block">
                    <span className="text-[0.6rem] tracking-[0.25em] uppercase text-white/60">Rating (1-5)</span>
                    <input type="number" min="1" max="5" value={r.rating ?? 5}
                      onChange={e => patch(i, "rating", parseInt(e.target.value || "5"))}
                      className="mt-1 w-full bg-black border border-white/15 px-3 py-2 text-sm focus:border-[#D4AF37] outline-none"
                      data-testid={`review-row-${i}-rating`} />
                  </label>
                  <label className="block sm:col-span-2">
                    <span className="text-[0.6rem] tracking-[0.25em] uppercase text-white/60">Relative time (e.g. "2 months ago")</span>
                    <input value={r.relative_time || ""} onChange={e => patch(i, "relative_time", e.target.value)}
                      className="mt-1 w-full bg-black border border-white/15 px-3 py-2 text-sm focus:border-[#D4AF37] outline-none"
                      data-testid={`review-row-${i}-time`} />
                  </label>
                  <label className="block sm:col-span-2">
                    <span className="text-[0.6rem] tracking-[0.25em] uppercase text-white/60">Review text</span>
                    <textarea rows={3} value={r.text || ""} onChange={e => patch(i, "text", e.target.value)}
                      className="mt-1 w-full bg-black border border-white/15 px-3 py-2 text-sm focus:border-[#D4AF37] outline-none"
                      data-testid={`review-row-${i}-text`} />
                  </label>
                </div>
                <button onClick={() => remove(i)} title="Delete"
                  className="p-2 text-red-300 hover:text-red-500" data-testid={`review-row-${i}-delete`}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {dirty && (
          <div className="mt-4 text-xs text-[#D4AF37] flex items-center gap-2" data-testid="reviews-dirty">
            You have unsaved changes — click "Save changes" above to publish.
          </div>
        )}
      </div>
    </div>
  );
}
